import { useAuthActions, useAuthContext } from "@contexts/auth";
import { endExpiredSession, readSessionToken } from "@contexts/auth/actions";
import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { trackPageView } from "@utils/activityTracker";
import {
  createSessionGuardedFetch,
  isTokenExpired,
  msUntilExpiry,
  platformApiBases,
} from "@utils/session";

// Espera a rota "assentar" antes de registrar o acesso: os redirecionamentos automáticos
// (ex.: /dashboard/curva -> /dashboard/curva/<obra>) não viram acessos duplicados.
const PAGE_VIEW_SETTLE_MS = 800;

// Uma vez por carga da aplicação: toda chamada às APIs da plataforma passa a avisar quando a
// sessão acabou (401, ou 403 com o token vencido), venha de qualquer tela.
let sessionGuardInstalled = false;
const installSessionGuard = () => {
  if (sessionGuardInstalled || typeof window === "undefined" || !window.fetch) return;
  sessionGuardInstalled = true;
  window.fetch = createSessionGuardedFetch({
    fetchImpl: window.fetch.bind(window),
    getToken: readSessionToken,
    onSessionEnded: endExpiredSession,
    bases: platformApiBases(),
  });
};

// `setTimeout` aceita no máximo ~24,8 dias; o token vive 2 h, mas um valor maior não pode
// virar disparo imediato.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const AppContainer = () => {
  const { user, cookieChecked } = useAuthContext();
  const { checkAuth } = useAuthActions();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    installSessionGuard();
    checkAuth();
  }, []);

  // O token vence mesmo sem nenhuma chamada: encerra a sessão na hora do vencimento e confere
  // de novo quando a aba volta ao foco (com o computador suspenso, o timer não roda).
  useEffect(() => {
    if (!user) return undefined;
    const token = readSessionToken();
    if (!token) return undefined;
    // Sem token é logout normal (já em andamento), não sessão vencida.
    const check = () => {
      const current = readSessionToken();
      if (current && isTokenExpired(current)) endExpiredSession();
    };
    const timer = setTimeout(check, Math.min(msUntilExpiry(token), MAX_TIMEOUT_MS));
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  useEffect(() => {
    if (cookieChecked) {
      if (pathname.includes('registerForm') || pathname.includes('clientForm')) return
      if (!user && !pathname.includes("/access")) {
        navigate("/access")
      } else if (user && !pathname.includes("/dashboard")) {
        if (user.roles.includes("bim")) navigate("/dashboard/bim/cadastro");
        if (user.roles.includes("costs")) navigate("/dashboard/costs/obras");
        if (user.roles.includes("ampli") || user.roles.includes("admin")) navigate("/dashboard")
      }
    }
  }, [user, cookieChecked])

  useEffect(() => {
    if (!user?._id || !pathname.startsWith("/dashboard")) return undefined;
    const timeout = setTimeout(() => trackPageView(pathname), PAGE_VIEW_SETTLE_MS);
    return () => clearTimeout(timeout);
  }, [pathname, user?._id])

  return <Outlet />
}

export default AppContainer;